import React, { useState } from 'react';
import { db, auth } from '../../firebase/config';
import { collection, writeBatch, doc, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

export default function BulkImporter() {
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [fileName, setFileName] = useState('');

  // 1. TEMPLATE GENERATOR
  const downloadTemplate = () => {
    const headers = "Title,Price,Category,Condition,Description,ImageURL\n";
    const sample = "Dell Latitude 3520,250,Electronics,Used - Good,Refurbished office laptop with charger.,https://via.placeholder.com/600?text=Laptop\n";
    const blob = new Blob([headers + sample], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nundinaria_bulk_template.csv';
    a.click();
  };

  // 2. CSV PARSER
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const rows = text.split('\n').slice(1); // Skip header
      const parsed = rows.map(row => {
        const cols = row.split(',');
        if (cols.length < 5) return null;
        return {
          title: cols[0]?.trim(),
          price: Number(cols[1]) || 0,
          category: cols[2]?.trim() || 'Other',
          condition: cols[3]?.trim() || 'Used - Good',
          description: cols[4]?.trim() || '',
          mainImage: cols[5]?.trim() || 'https://via.placeholder.com/600?text=No+Image+Provided'
        };
      }).filter(item => item !== null && item.title);
      setCsvData(parsed);
    };
    reader.readAsText(file);
  };

  // 3. THE POWER MOVE: FIRESTORE BATCH WRITE
  const handleBulkUpload = async () => {
    if (!auth.currentUser) return alert("Please log in.");
    if (csvData.length === 0) return alert("No valid data found in CSV.");
    if (csvData.length > 100) return alert("Please limit bulk uploads to 100 items at a time.");

    setImporting(true);
    const batch = writeBatch(db);

    try {
      csvData.forEach((item) => {
        const newDocRef = doc(collection(db, "listings")); // Generate unique ID
        batch.set(newDocRef, {
          ...item,
          sellerId: auth.currentUser.uid,
          sellerEmail: auth.currentUser.email,
          status: 'active',
          views: 0,
          shippingAvailable: true,
          createdAt: serverTimestamp(),
          isBulkImport: true
        });
      });

      await batch.commit(); // Push all listings at once!
      alert(`Success! ${csvData.length} items have been listed.`);
      navigate('/profile');
    } catch (error) {
      console.error("Bulk Upload Error:", error);
      alert("Failed to import items. Check your CSV format.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '40px auto', padding: '0 20px' }}>
      <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', textAlign: 'center' }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '32px' }}>Power Seller: Bulk Importer</h1>
        <p style={{ color: '#6c757d', marginBottom: '30px' }}>Upload a CSV file to list up to 100 pieces of gear instantly.</p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '40px' }}>
          <button onClick={downloadTemplate} style={secondaryBtn}>📥 Download Template</button>
          <label style={primaryBtn}>
            📁 {fileName || 'Select CSV File'}
            <input type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} />
          </label>
        </div>

        {csvData.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <div style={{ backgroundColor: '#e6f2ff', padding: '15px', borderRadius: '8px', border: '1px solid #b8daff', marginBottom: '20px' }}>
              <strong style={{ color: '#004085' }}>Ready to Import: {csvData.length} Items</strong>
            </div>
            
            <div style={{ maxHeight: '300px', overflowY: 'auto', textAlign: 'left', border: '1px solid #eee', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={thStyle}>Title</th>
                    <th style={thStyle}>Price</th>
                    <th style={thStyle}>Category</th>
                  </tr>
                </thead>
                <tbody>
                  {csvData.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={tdStyle}>{item.title}</td>
                      <td style={tdStyle}>${item.price}</td>
                      <td style={tdStyle}>{item.category}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button 
              onClick={handleBulkUpload} 
              disabled={importing}
              style={{ ...uploadBtn, marginTop: '30px' }}
            >
              {importing ? 'Processing Batch...' : `🚀 Start Bulk Listing (${csvData.length} Items)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Internal Styles
const primaryBtn = { padding: '12px 24px', backgroundColor: '#1a1a1a', color: 'white', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'inline-block' };
const secondaryBtn = { padding: '12px 24px', backgroundColor: 'white', color: '#1a1a1a', border: '2px solid #1a1a1a', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' };
const uploadBtn = { width: '100%', padding: '16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '900', fontSize: '18px', cursor: 'pointer' };
const thStyle = { padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' };
const tdStyle = { padding: '10px', color: '#495057' };